import React, { useEffect, useMemo, useState } from 'react';
import { EXECUTION_STEPS_TITLE } from '../constant';
import styles from '../../../styles/automation/executionsteps.module.scss';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import StepDetail from './StepDetail';
import ButtonComponent from '../../customButton';
import { setActivePage } from '../../../features/navigation/navigationSlice';
import DEFAULT_ROUTES from '../../../constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useVariables from '../ReplayPage/hooks/useVariables';
import { replaceFileRefsWithNames } from '../../../utils/automation/variableUtils';
import { isDynamicStep } from '../../AutomationStudio/utils/common';

function buildUuidToDisplayStepNumberMap(steps) {
  const map = new Map();
  (steps || []).forEach((step, idx) => {
    const stepIndexNum = Number(step?.stepIndex);
    const zeroBased = Number.isFinite(stepIndexNum) ? stepIndexNum : idx;
    const n = zeroBased + 1;
    // stepId wins over id when both are present — stepId is the canonical dynamic-step identifier in the plan.
    for (const raw of [step?.stepId, step?.id]) {
      const trimmed = raw != null ? String(raw).trim() : '';
      const k = trimmed ? trimmed.toLowerCase() : '';
      if (k && !map.has(k)) map.set(k, n);
    }
  });
  return map;
}

function StepItem({ step = {}, expanded = false, onToggle = () => {}, dynamicResult = null, uuidToDisplayStep = null }) {
  const { convertToDisplay } = useVariables();
  const fileSuggestions = useSelector((state) => state.replay?.fileSuggestions ?? []);
  const description = useMemo(() => {
    const raw = step?.extractedData?.stepDescription || step?.args?.stepDescription || '';
    return replaceFileRefsWithNames(convertToDisplay(raw), fileSuggestions);
  }, [step?.extractedData?.stepDescription, step?.args?.stepDescription, convertToDisplay, fileSuggestions]);
  const isDynamic = useMemo(
    () => isDynamicStep(step, dynamicResult),
    [dynamicResult?.dynamicStepIds, step?.stepId, step?.id],
  );
  const stepNumber = useMemo(() => {
    const n = Number(step?.stepIndex);
    return (Number.isFinite(n) ? n : 0) + 1;
  }, [step?.stepIndex]);
  const customStep = step?.customStep;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <div className={styles.stepContainer}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-level={stepNumber}
        aria-label={description}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        className={`${styles.stepItem} ${expanded ? styles.expandedStepItem : ''}`}
      >
        <div className={styles.leftSide}>
          <span className={styles.stepNumber}>{stepNumber}</span>
        </div>
        <div className={styles.middleSide}>
          <span className={styles.stepDescription}>{description}</span>
        </div>
        <div className={styles.rightSide}>
          {isDynamic && (
            <span className={styles.dynamicTag} role="status" aria-label="Dynamic" title="Dynamic">
              Dynamic
            </span>
          )}
          {customStep && <span className={styles.customStepTag}>custom</span>}
          <div className={styles.chevronContainer}>
            <ChevronDownIcon className={`icon slate900 ${expanded ? styles.chevronUp : ''}`} />
          </div>
        </div>
      </div>

      <div className={styles.stepContent}>
        {expanded && <StepDetail step={step} dynamicResult={dynamicResult} uuidToDisplayStep={uuidToDisplayStep} />}
      </div>
    </div>
  );
}

export default function ExecutionSteps({ steps = [], testCaseId = null, dynamicResult = null }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedProject = useSelector((state) => state?.projects?.selectedProject);

  const [expandedMap, setExpandedMap] = useState([]);

  useEffect(() => {
    setExpandedMap((prev) => {
      const next = new Array(steps.length).fill(false);
      for (let i = 0; i < Math.min(prev.length, next.length); i++) next[i] = !!prev[i];
      return next;
    });
  }, [steps]);

  const allExpanded = useMemo(() => expandedMap.length > 0 && expandedMap.every(Boolean), [expandedMap]);
  const noneExpanded = useMemo(() => expandedMap.every((v) => !v), [expandedMap]);
  const uuidToDisplayStep = useMemo(() => buildUuidToDisplayStepNumberMap(steps), [steps]);

  const toggleStep = (idx) => {
    setExpandedMap((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  const handleExpandAll = () => {
    setExpandedMap((prev) => prev.map(() => !allExpanded)); 
  };

  const openAutomationStudio = () => {
    dispatch(setActivePage('Automation Studio'));
    const url = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject?.id}${DEFAULT_ROUTES.AUTOMATION_STUDIO}/${testCaseId}`;
    window.open(url, '_blank');
  };

  return (
    <div className={styles.stepsContainer}>
      <div className={styles.headerContainer}>
        <span
          role="title"
          aria-level={EXECUTION_STEPS_TITLE}
          aria-label={EXECUTION_STEPS_TITLE}
          className={styles.title}
        >
          {EXECUTION_STEPS_TITLE}
          <span
            role="button"
            tabIndex={0}
            aria-label={allExpanded ? 'Collapse All' : 'Expand All'}
            className={styles.expandAllButton}
            onClick={handleExpandAll}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleExpandAll()}
          >
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </span>
        </span>
        <div className={styles.buttonContainer}>
          <ButtonComponent ctaText="Automation Studio" className="white bg-primary" onClick={openAutomationStudio} />
        </div>
      </div>

      <div className={styles.stepsList}>
        {(steps || []).map((step, idx) => (
          <StepItem
            key={step?.id ?? idx}
            step={step}
            expanded={!!expandedMap[idx]}
            onToggle={() => toggleStep(idx)}
            dynamicResult={dynamicResult}
            uuidToDisplayStep={uuidToDisplayStep}
          />
        ))}
      </div>
    </div>
  );
}
